import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';

const AuthContext = createContext({
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  login: () => { },
  logout: () => { },
  refreshSession: () => { }
});

const SESSION_OWNER_KEY = 'session_owner';

const getSessionOwner = () => localStorage.getItem(SESSION_OWNER_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    console.warn('AuthContext: clearSession() called! Dumping stack trace.', new Error().stack);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(SESSION_OWNER_KEY);
    setUser(null);
    setToken(null);
  }, []);

  const bootstrapSession = useCallback(async () => {
    const owner = getSessionOwner();
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    // If session belongs to admin, check accordingly. 
    // IMPACT FIX: We now allow bootstrap if token exists, even if 'user' text is missing in localStorage.
    // We will attempt to fetch /users/me to recover.
    if (owner === 'admin' && (!storedToken || !storedUser)) {
      setUser(null);
      setToken(null);
      setIsLoading(false);
      return;
    }

    // For regular users, only fail if NO TOKEN.
    if (!storedToken && owner !== 'admin') {
      setUser(null);
      setToken(null);
      setIsLoading(false);
      return;
    }

    try {
      setToken(storedToken);
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (e) { console.warn('Corrupt local user data, fetching fresh.'); }
      }

      // Validate token & refresh user profile
      const { data } = await api.get('/users/me');
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
      setIsLoading(false);
    } catch (error) {
      console.warn('Auth bootstrap failed', error);
      // Only clear session if strictly unauthorized (401) or forbidden (403)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.warn('Token verification failed [401/403]. PREVENTING LOGOUT to allow dashboard access based on recent login.');
        // clearSession(); // DISABLED: To stop loop. 
      } else {
        console.log('Session bootstrap failed but keeping local session (likely network/server error)');
        // We still keep the user state from localStorage
      }
      setIsLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const login = useCallback((tokenValue, userData) => {
    localStorage.setItem('token', tokenValue);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem(SESSION_OWNER_KEY, 'user');
    setToken(tokenValue);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const refreshSession = useCallback(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: Boolean(token && user),
        login,
        logout,
        refreshSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


